import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  BrowserMultiFormatReader,
} from '@zxing/browser'
import type { IScannerControls } from '@zxing/browser'
import { NotFoundException, Result } from '@zxing/library'
import Loading from '../common/Loading'
import {
  createQrTagEvent,
  scanQrTagDigitCode,
} from '../../modules/QrTag'
import { getErrorString } from '../../utils/errorUtils'
import type { QrTagEventType } from '../../constants/Constants'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { fetchGroupEventTypes } from '../../modules/QrTagEventTypes'
import './QrTagEventRecorder.css'

type Props = {
  group: IGroup
  onEventCreated?: () => Promise<void> | void
}

type Coordinates = {
  latitude: number
  longitude: number
}

const INITIAL_STATUS = 'QRコードを読み取ってください'

const QrTagEventRecorder = ({
  group,
  onEventCreated,
}: Props) => {
  const dispatch = useAppDispatch()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null)
  const handleDigitDetectedRef = useRef<(code: string) => Promise<void> | void>()
  const scannerControlsRef = useRef<IScannerControls | null>(null)
  const snapshotRef = useRef<string | null>(null)

  const [statusMessage, setStatusMessage] = useState<string>(INITIAL_STATUS)
  const [qrTag, setQrTag] = useState<IQrTag | null>(null)
  const [location, setLocation] = useState<Coordinates | null>(null)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [eventType, setEventType] = useState<QrTagEventType | ''>('')
  const [memo, setMemo] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isScannerReady, setIsScannerReady] = useState(false)
  const eventTypesEntry = useAppSelector((state) => state.qrTagEventTypes.entries[group.id])
  const eventTypes = useMemo(
    () => eventTypesEntry?.values ?? [],
    [eventTypesEntry],
  )
  const eventTypesStatus = eventTypesEntry?.status ?? 'idle'
  const eventTypesError = eventTypesEntry?.error ?? null
  const isEventTypesLoading = eventTypesStatus === 'loading'
  const isEventTypesError = eventTypesStatus === 'failed'
  const isEventTypesEmpty = eventTypesStatus === 'succeeded' && eventTypes.length === 0
  const isEventTypeSelectionDisabled = isEventTypesLoading || isEventTypesError || eventTypes.length === 0

  const cleanupMedia = useCallback(() => {
    if (scannerControlsRef.current) {
      scannerControlsRef.current.stop()
      scannerControlsRef.current = null
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop())
      mediaStreamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.srcObject = null
      if (snapshotRef.current) {
        videoRef.current.poster = snapshotRef.current
      }
    }
  }, [])

  useEffect(() => () => {
    cleanupMedia()
  }, [cleanupMedia])

  useEffect(() => {
    dispatch(fetchGroupEventTypes({ groupId: group.id }))
  }, [dispatch, group.id])

  useEffect(() => {
    if (eventTypes.length === 0) {
      if (eventType) {
        setEventType('')
      }
      return
    }
    if (!eventType || !eventTypes.some((type) => type.name === eventType)) {
      setEventType(eventTypes[0]?.name ?? '')
    }
  }, [eventTypes, eventType])

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('位置情報の取得に対応していないブラウザです。')
      return
    }

    setLocationError(null)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      },
      (geoError) => {
        console.error('Failed to get location', geoError)
        setLocationError('位置情報の取得に失敗しました。再試行してください。')
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
      },
    )
  }, [])

  const handleReloadEventTypes = useCallback(() => {
    dispatch(fetchGroupEventTypes({ groupId: group.id, force: true }))
  }, [dispatch, group.id])

  const startDecoding = useCallback(async () => {
    if (!videoRef.current) return

    if (!codeReaderRef.current) {
      codeReaderRef.current = new BrowserMultiFormatReader()
    }

    if (scannerControlsRef.current) {
      scannerControlsRef.current.stop()
      scannerControlsRef.current = null
    }

    try {
      const controls = await codeReaderRef.current.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (result: Result | undefined, err, controlsFromCallback) => {
          if (controlsFromCallback) {
            scannerControlsRef.current = controlsFromCallback
          }
          if (result) {
            const text = result.getText()
            if (text) {
              setStatusMessage('QRコードを検出しました')
              setError(null)
              if (videoRef.current) {
                const video = videoRef.current
                const canvas = document.createElement('canvas')
                canvas.width = video.videoWidth || video.clientWidth
                canvas.height = video.videoHeight || video.clientHeight
                const ctx = canvas.getContext('2d')
                if (ctx && canvas.width && canvas.height) {
                  ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
                  snapshotRef.current = canvas.toDataURL('image/png')
                  video.poster = snapshotRef.current
                }
              }
              scannerControlsRef.current?.stop()
              scannerControlsRef.current = null
              const handler = handleDigitDetectedRef.current
              if (handler) {
                Promise.resolve(handler(text)).catch(() => {
                  // handler内でエラー処理が行われるためここでは握りつぶす
                })
              }
            }
          }

          if (err && !(err instanceof NotFoundException)) {
            console.error('QR decode error', err)
          }
        },
      )
      scannerControlsRef.current = controls
    } catch (decodeError) {
      console.error('Failed to start QR decoding', decodeError)
      setError('QRコードの読み取りを開始できませんでした。')
    }
  }, [])

  const prepareCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('カメラの利用に対応していないブラウザです。')
      return
    }

    try {
      setStatusMessage('カメラを起動しています...')
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: {
            ideal: 'environment',
          },
        },
      })
      mediaStreamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
      setStatusMessage(INITIAL_STATUS)
      setIsScannerReady(true)
      await startDecoding()
    } catch (cameraError) {
      console.error('Failed to prepare camera', cameraError)
      setError('カメラの利用を許可してください。')
    }
  }, [startDecoding])

  const resetScanning = useCallback(async () => {
    if (scannerControlsRef.current) {
      scannerControlsRef.current.stop()
      scannerControlsRef.current = null
    }
    setQrTag(null)
    setMemo('')
    setStatusMessage(INITIAL_STATUS)
    snapshotRef.current = null
    if (videoRef.current) {
      videoRef.current.poster = ''
    }
    await startDecoding()
  }, [startDecoding])

  const handleDigitDetected = useCallback(async (code: string) => {
    try {
      setStatusMessage('QRタグを照合しています...')
      const response = await scanQrTagDigitCode(group.id, code)
      setQrTag(response.qr_tag)
      setStatusMessage('イベント情報を入力してください')
      setError(null)
    } catch (scanError) {
      const message = getErrorString(scanError)
      setError(message)
      await resetScanning()
    }
  }, [group.id, resetScanning])

  useEffect(() => {
    handleDigitDetectedRef.current = handleDigitDetected
  }, [handleDigitDetected])

  useEffect(() => {
    prepareCamera().catch((err) => console.error(err))
    requestLocation()
  }, [prepareCamera, requestLocation])

  const handleSubmit = async () => {
    if (!qrTag) {
      setError('QRタグが読み取られていません。')
      return
    }
    if (!eventType || !eventTypes.some((type) => type.name === eventType)) {
      setError('イベント種別を選択してください。')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await createQrTagEvent(group.id, qrTag.id, {
        event_type: eventType,
        memo: memo || undefined,
        latitude: location?.latitude,
        longitude: location?.longitude,
      })
      setStatusMessage('イベントを記録しました。')
      await onEventCreated?.()
    } catch (submitError) {
      const message = getErrorString(submitError)
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="qr-tag-event-recorder" data-testid="qr-tag-event-recorder">
      <div className="qr-tag-scanner-body">
        <Loading show={isSubmitting || !isScannerReady} />
        <div className="scanner-section">
          <video ref={videoRef} className="scanner-video" autoPlay muted playsInline />
          <div className="scanner-status">{statusMessage}</div>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              requestLocation()
            }}
          >
            位置情報を再取得
          </button>
          {location && (
            <div className="location-info">
              取得位置:
              {' '}
              {location.latitude.toFixed(6)}
              ,
              {' '}
              {location.longitude.toFixed(6)}
            </div>
          )}
          {locationError && (
            <div className="scanner-error">
              {locationError}
            </div>
          )}
          {error && (
            <div className="scanner-error">
              {error}
            </div>
          )}
          <button
            type="button"
            className="btn btn-link"
            onClick={() => {
              resetScanning().catch(() => {
                setError('再スキャンの準備に失敗しました。')
              })
            }}
          >
            再スキャンする
          </button>
        </div>
        {qrTag && (
          <div className="scanner-form">
            <div className="form-group">
              <label className="form-label">対象QRタグ</label>
              <div className="form-static">
                {qrTag.name}
                {' '}
                (
                {qrTag.qr_code_info?.digit_code || '番号不明'}
                )
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="qr-tag-event-type">
                イベント種別
              </label>
              <select
                id="qr-tag-event-type"
                className="form-control"
                value={eventType || ''}
                disabled={isEventTypeSelectionDisabled}
                onChange={(evt) => setEventType(evt.target.value as QrTagEventType)}
              >
                {eventTypes.map((option) => (
                  <option key={option.name} value={option.name}>{option.name}</option>
                ))}
              </select>
              {isEventTypesLoading && (
                <div className="form-hint">
                  イベントタイプを読み込み中です...
                </div>
              )}
              {isEventTypesEmpty && (
                <div className="scanner-warning">
                  イベントタイプが未設定です。設定完了までイベント登録はできません。
                </div>
              )}
              {isEventTypesError && (
                <div className="scanner-error">
                  {eventTypesError || 'イベントタイプの取得に失敗しました。'}
                  {' '}
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={handleReloadEventTypes}
                  >
                    再読み込み
                  </button>
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="qr-tag-event-memo">
                メモ
              </label>
              <textarea
                id="qr-tag-event-memo"
                className="form-control"
                rows={3}
                value={memo}
                onChange={(evt) => setMemo(evt.target.value)}
                placeholder="任意でメモを入力"
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={isSubmitting || isEventTypeSelectionDisabled}
              onClick={handleSubmit}
            >
              イベントを記録する
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default QrTagEventRecorder
